import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  OAuthProvider
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { auth, googleProvider, db } from './firebaseConfig';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children, siteId = 'ubunited' }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const appleProvider = new OAuthProvider('apple.com');

  const signup = (email, password) => createUserWithEmailAndPassword(auth, email, password);
  const login = (email, password) => signInWithEmailAndPassword(auth, email, password);
  const loginWithGoogle = () => signInWithPopup(auth, googleProvider);
  const loginWithApple = () => signInWithPopup(auth, appleProvider);
  const logout = () => signOut(auth);

  const getUserProfile = async (userId) => {
    try {
      const docSnap = await getDoc(doc(db, 'users', userId));
      if (docSnap.exists()) return docSnap.data();

      // Fallback: check if a profile exists under a different UID for the same email
      const user = auth.currentUser;
      if (user?.email) {
        const emailQuery = query(collection(db, 'users'), where('email', '==', user.email));
        const snapshot = await getDocs(emailQuery);
        if (!snapshot.empty) {
          const existingData = snapshot.docs[0].data();
          // Migrate profile to current UID
          await setDoc(doc(db, 'users', userId), {
            ...existingData,
            updatedAt: new Date().toISOString()
          }, { merge: true });
          return existingData;
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  };

  const updateUserProfile = async (userId, updates) => {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      ...updates,
      updatedAt: new Date().toISOString(),
      signupSite: siteId
    }, { merge: true });
    const updatedProfile = await getUserProfile(userId);
    setUserProfile(updatedProfile);
    return true;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        const profile = await getUserProfile(user.uid);
        setUserProfile(profile);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    loginWithGoogle,
    loginWithApple,
    logout,
    getUserProfile,
    updateUserProfile,
    siteId
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
